// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import type { CSSProperties, KeyboardEvent, MouseEvent } from 'react'

/**
 * D-CRX-70 — one behaviour for every CareRx worklist: the ROW opens the record.
 *
 * Each table had grown its own idea of how to reach a record — a link button at the far right, a
 * "Review" button, a clickable row, or nothing at all — so the same gesture did something
 * different on every tab and on two of them did nothing. Hunting for a button to read a chart is
 * the annoyance; this makes the row itself the target everywhere.
 *
 * Spread onto <tr>:  <tr {...rowOpen(() => setOpen(row))}>
 * And put {...rowActions} on any cell with its own controls, so clicking Resolve resolves rather
 * than also opening the record behind it.
 */
export function rowOpen(open: () => void, extra?: CSSProperties) {
  return {
    onClick: open,
    style: { cursor: 'pointer', ...extra } as CSSProperties,
    title: 'Open the record',
    // Keyboard parity: a row that only responds to a mouse is not reachable for anyone tabbing.
    tabIndex: 0,
    onKeyDown: (e: { key: string; preventDefault: () => void }) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open() }
    },
  }
}

/** The keys the row reacts to — the only ones a cell with its own controls must keep to itself. */
export const ROW_OPEN_KEYS: ReadonlySet<string> = new Set(['Enter', ' '])

/** Spread onto a cell that holds buttons/inputs of its own. Enter and Space stop here: without that,
 *  Space typed in a field (or Enter on a button) inside the cell bubbled to the row and opened the
 *  record. ONLY those two (review 2026-10-02, S11): stopping every key also swallowed Escape, so a
 *  panel opened from the cell ("Remove from PA approved") could not be closed from the keyboard. */
export const rowActions = {
  onClick: (e: MouseEvent) => e.stopPropagation(),
  onKeyDown: (e: KeyboardEvent) => { if (ROW_OPEN_KEYS.has(e.key)) e.stopPropagation() },
}
